import {
	BUSINESS_PLAN_FEATURES,
	FREE_PLAN_FEATURES,
	PRO_PLAN_FEATURES,
	PRO_TIER_FEATURES,
	pricingPlans,
} from '@/features/pricing/constants/plans';
import { PlanFeatures } from '@/features/pricing/types';

import { COPY } from '@/shared/constants/copies';
import { PlanType } from '@/shared/services/payment/payment.types';
import { formatWholeUsd } from '@/shared/utils/format-money';

export const PLAN_TYPE_TO_DISPLAY_NAME: Record<PlanType, string> = {
	[PlanType.FREE]: COPY.pricing.planNameFree,
	[PlanType.PRO_MONTHLY]: COPY.pricing.planNameStarterMonthly,
	[PlanType.PRO_YEARLY]: COPY.pricing.planNameStarterYearly,
	[PlanType.UNLIMITED_PRO_MONTHLY]: COPY.pricing.planNameBusinessMonthly,
	[PlanType.UNLIMITED_PRO_YEARLY]: COPY.pricing.planNameBusinessYearly,
	[PlanType.PREMIUM_PRO_MONTHLY]: COPY.pricing.planNameProMonthly,
	[PlanType.PREMIUM_PRO_YEARLY]: COPY.pricing.planNameProYearly,
};

/** Tier name without the billing interval — "Business", not "Business Monthly". */
export const PLAN_TYPE_TO_TIER_LABEL: Record<PlanType, string> = {
	[PlanType.FREE]: COPY.pricing.tierFree,
	[PlanType.PRO_MONTHLY]: COPY.pricing.tierStarter,
	[PlanType.PRO_YEARLY]: COPY.pricing.tierStarter,
	[PlanType.UNLIMITED_PRO_MONTHLY]: COPY.pricing.tierBusiness,
	[PlanType.UNLIMITED_PRO_YEARLY]: COPY.pricing.tierBusiness,
	[PlanType.PREMIUM_PRO_MONTHLY]: COPY.pricing.tierPro,
	[PlanType.PREMIUM_PRO_YEARLY]: COPY.pricing.tierPro,
};

export function getPlanLabel(planType: PlanType | null | undefined): string {
	return planType ? (PLAN_TYPE_TO_TIER_LABEL[planType] ?? '') : '';
}

export type PlanConfig = {
	name: string;
	features: PlanFeatures;
	buttonText: string;
	prices: {
		monthly: string;
		yearly: string;
	};
	planIds: {
		monthly: string;
		yearly: string;
	};
	stripePriceIds: {
		monthly: string | null;
		yearly: string | null;
	};
	priceSubtext?: string | null;
};

export type StaticPlanEntry = {
	name: string;
	features: PlanFeatures;
	buttonText: string;
	planIds: {
		monthly: string;
		yearly: string;
	};
	priceSubtext?: string | null;
	/** Fallback price shown when the plan has no dynamic API entry (e.g. Free). */
	staticPrice?: string;
};

export const PLAN_STATIC_CONFIG: StaticPlanEntry[] = [
	{
		name: COPY.pricing.tierFree,
		features: FREE_PLAN_FEATURES,
		buttonText: COPY.pricing.getStartedCta,
		planIds: {
			monthly: pricingPlans.free.id,
			yearly: pricingPlans.free.id,
		},
		priceSubtext: null,
		staticPrice: formatWholeUsd(0),
	},
	{
		name: COPY.pricing.tierStarter,
		features: PRO_PLAN_FEATURES,
		buttonText: COPY.pricing.getStartedCta,
		planIds: {
			monthly: pricingPlans.pro_monthly.id,
			yearly: pricingPlans.pro_yearly.id,
		},
		priceSubtext: COPY.pricing.perMonth,
	},
	{
		name: COPY.pricing.tierBusiness,
		features: BUSINESS_PLAN_FEATURES,
		buttonText: COPY.pricing.getStartedCta,
		planIds: {
			monthly: pricingPlans.unlimited_pro_monthly.id,
			yearly: pricingPlans.unlimited_pro_yearly.id,
		},
		priceSubtext: COPY.pricing.perMonth,
	},
	{
		name: COPY.pricing.tierPro,
		features: PRO_TIER_FEATURES,
		buttonText: COPY.pricing.getStartedCta,
		planIds: {
			monthly: pricingPlans.premium_pro_monthly.id,
			yearly: pricingPlans.premium_pro_yearly.id,
		},
		priceSubtext: COPY.pricing.perMonth,
	},
];
